import React, { useEffect, useRef, useState } from "react";
import { ADSENSE_CLIENT, isAdsenseEnabled, getSlotId, onAdsenseConfigChange, loadAdsenseConfig, pushSlot } from "../lib/adsense";

/**
 * Slot AdSense responsive avec lazy-load (IntersectionObserver).
 * Ne se monte qu'à l'approche du viewport pour préserver les Core Web Vitals.
 *
 * Props:
 *  - slotKey: clé logique ("homeBanner" | "inFeed" | "sidebar" | "trackBottom" | "articleInline")
 *             ou directement un slot ID via prop "slot"
 *  - slot: data-ad-slot brut (override)
 *  - format: "auto" | "rectangle" | "horizontal" | "vertical" | "fluid"
 *  - layout: ex "in-article"
 *  - layoutKey: pour in-feed
 *  - className, testid, minHeight (réservation pour CLS)
 *  - alwaysShow: bypass l'enabled (preview admin)
 */
export const AdSenseSlot = ({
  slotKey,
  slot,
  format = "auto",
  layout,
  layoutKey,
  className = "",
  testid,
  alwaysShow = false,
  minHeight = 90,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [, force] = useState(0);

  useEffect(() => { loadAdsenseConfig(); }, []);
  useEffect(() => onAdsenseConfigChange(() => force((n) => n + 1)), []);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setVisible(true); });
    }, { rootMargin: "200px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const resolvedSlot = slot || (slotKey ? getSlotId(slotKey) : "");

  useEffect(() => {
    if (!visible || !resolvedSlot) return;
    if (!alwaysShow && !isAdsenseEnabled()) return;
    const t = setTimeout(() => pushSlot(ref.current?.querySelector(".adsbygoogle")), 200);
    return () => clearTimeout(t);
  }, [visible, resolvedSlot, alwaysShow]);

  // Render conditions
  if (!alwaysShow && !isAdsenseEnabled()) return null;
  if (!resolvedSlot) return null;

  return (
    <div
      ref={ref}
      className={`adsense-slot ${className}`}
      style={{ minHeight, display: "block", textAlign: "center" }}
      data-testid={testid || `adsense-${slotKey || resolvedSlot}`}
      aria-label="Publicité"
    >
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={resolvedSlot}
        data-ad-format={format}
        data-full-width-responsive="true"
        {...(layout ? { "data-ad-layout": layout } : {})}
        {...(layoutKey ? { "data-ad-layout-key": layoutKey } : {})}
      />
    </div>
  );
};

export default AdSenseSlot;
